import { useEffect, useRef, useState } from "react";
import { Bell, Menu, ShieldAlert, CheckCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { fmtDateTimeShort } from "@/lib/dates";

const TYPE_LABEL = {
  shift_published: "New shift assigned",
  shift_concern: "Shift concern flagged",
  rota_published: "Rota published",
  safeguarding_concern: "Safeguarding concern",
  safeguarding_escalated: "Safeguarding escalated",
  incident_reported: "New incident",
  incident_escalated: "Incident escalated",
  complaint_received: "New complaint",
  complaint_escalated: "Complaint escalated",
  care_plan_review_due: "Care plan review due",
  dbs_expiring: "DBS expiring",
  service_user_updated: "Service user updated",
  family_message: "New family message",
  care_team_message: "Care team message",
  opp_share_viewed: "OPP share viewed",
};

export default function Topbar({ title, onMenuClick }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = () => {
    api
      .get("/me/notifications")
      .then(({ data }) => {
        setItems(data.items || []);
        setUnread(data.unread_count || 0);
      })
      .catch(() => {});
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const onClickItem = async (n) => {
    if (!n.read) {
      try {
        await api.post(`/me/notifications/${n.id}/read`);
        setItems((xs) => xs.map((x) => (x.id === n.id ? { ...x, read: true } : x)));
        setUnread((c) => Math.max(0, c - 1));
      } catch {
        /* ignore */
      }
    }
    setOpen(false);
    if (n.link) navigate(n.link);
  };

  const markAll = async () => {
    try {
      await api.post("/me/notifications/mark-all-read");
      setItems((xs) => xs.map((x) => ({ ...x, read: true })));
      setUnread(0);
    } catch {
      /* ignore */
    }
  };

  return (
    <header
      className="topbar sticky top-0 z-20 h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 md:px-10 md:ml-64 no-print"
    >
      <div className="flex items-center gap-3">
        <button
          onClick={onMenuClick}
          className="md:hidden p-2 rounded-md hover:bg-slate-100"
          data-testid="topbar-menu-button"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5 text-slate-700" />
        </button>
        <h2 className="font-heading text-lg sm:text-xl font-semibold text-slate-900" data-testid="page-title">
          {title}
        </h2>
      </div>
      <div className="flex items-center gap-2">
        {user && user.role !== "family_member" && (
          <Link
            to="/safeguarding/new"
            data-testid="raise-safeguarding-button"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-semibold rounded-md bg-red-50 text-red-700 hover:bg-red-100 border border-red-200 transition-colors"
            title="Raise a safeguarding concern"
          >
            <ShieldAlert className="h-4 w-4" /> Raise concern
          </Link>
        )}
        <div className="relative" ref={ref}>
          <button
            className="relative p-2 rounded-md hover:bg-slate-100"
            aria-label="Notifications"
            data-testid="topbar-notifications-button"
            onClick={() => setOpen((v) => !v)}
          >
            <Bell className="h-5 w-5 text-slate-700" />
            {unread > 0 && (
              <span data-testid="topbar-notifications-badge" className="absolute -top-0.5 -right-0.5 h-4 min-w-[16px] rounded-full bg-red-500 text-white text-[10px] flex items-center justify-center px-1">
                {unread > 99 ? "99+" : unread}
              </span>
            )}
          </button>
          {open && (
            <div
              data-testid="topbar-notifications-panel"
              className="absolute right-0 mt-2 w-[360px] max-h-[70vh] overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl"
            >
              <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
                <p className="text-sm font-semibold text-slate-900">Notifications</p>
                <button
                  onClick={markAll}
                  disabled={unread === 0}
                  data-testid="topbar-notifications-mark-all"
                  className="text-xs text-[#0D7377] hover:underline disabled:text-slate-400 disabled:no-underline inline-flex items-center gap-1"
                >
                  <CheckCheck className="h-3.5 w-3.5" /> Mark all read
                </button>
              </div>
              <ul className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
                {items.length === 0 && (
                  <li className="px-3 py-6 text-center text-sm text-slate-500" data-testid="topbar-notifications-empty">
                    Nothing here yet.
                  </li>
                )}
                {items.map((n) => (
                  <li key={n.id}>
                    <button
                      onClick={() => onClickItem(n)}
                      data-testid={`notification-item-${n.id}`}
                      className={`w-full text-left px-3 py-2.5 hover:bg-slate-50 flex items-start gap-2 ${n.read ? "opacity-70" : ""}`}
                    >
                      {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-[#0D7377] shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          {TYPE_LABEL[n.type] || (n.type || "").replace(/_/g, " ")}
                        </p>
                        <p className="text-sm text-slate-800 break-words whitespace-pre-wrap">{n.message}</p>
                        <p className="text-[10px] text-slate-400 mt-0.5">{fmtDateTimeShort(n.created_at)}</p>
                      </div>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
        <div className="hidden sm:flex items-center gap-2 ml-2 pl-3 border-l border-slate-200">
          <div className="h-8 w-8 rounded-full bg-[#0D7377] text-white flex items-center justify-center text-xs font-semibold">
            {(user?.full_name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2)}
          </div>
          <div className="leading-tight">
            <p className="text-sm font-medium text-slate-900">{user?.full_name}</p>
            <p className="text-[11px] text-slate-500 capitalize">{(user?.role || "").replace(/_/g, " ")}</p>
          </div>
        </div>
      </div>
    </header>
  );
}
